import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileAttachments = ({ attachments, onUpload, onDownload, onDelete, canEdit }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const getFileIcon = (type) => {
    if (type?.includes('image')) return 'Image';
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('document') || type?.includes('word')) return 'FileText';
    if (type?.includes('spreadsheet') || type?.includes('excel')) return 'FileSpreadsheet';
    if (type?.includes('video')) return 'Video';
    if (type?.includes('audio')) return 'Music';
    return 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = async (files) => {
    if (!canEdit) return;
    
    for (const file of files) {
      setUploadProgress({ name: file?.name, progress: 0 });
      
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev?.progress >= 100) {
            clearInterval(interval);
            setTimeout(() => setUploadProgress(null), 1000);
            return prev;
          }
          return { ...prev, progress: prev?.progress + 10 };
        });
      }, 200);

      await onUpload(file);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Paperclip" size={20} />
          File Attachments ({attachments?.length})
        </h2>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            Upload
          </Button>
        )}
      </div>
      {/* Upload Area */}
      {canEdit && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 mb-6 transition-colors duration-200 ${
            isDragOver 
              ? 'border-primary bg-primary/5' :'border-border hover:border-border-active'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-foreground mb-1">
              Drag and drop files here, or click to select
            </p>
            <p className="text-xs text-muted-foreground">
              Maximum file size: 10MB. Supported formats: PDF, DOC, XLS, JPG, PNG
            </p>
          </div>
          <input
            id="file-upload"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
          />
        </div>
      )}
      {/* Upload Progress */}
      {uploadProgress && (
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Uploading: {uploadProgress?.name}
            </span>
            <span className="text-sm text-muted-foreground">
              {uploadProgress?.progress}%
            </span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress?.progress}%` }}
            />
          </div>
        </div>
      )}
      {/* File List */}
      <div className="space-y-3">
        {attachments?.map((file) => (
          <div key={file?.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Icon name={getFileIcon(file?.type)} size={20} className="text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{file?.name}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{formatFileSize(file?.size)}</span>
                  <span>Uploaded by {file?.uploadedBy}</span>
                  <span>{new Date(file.uploadedAt)?.toLocaleDateString()}</span>
                  {file?.version > 1 && (
                    <span className="px-1.5 py-0.5 bg-accent text-accent-foreground rounded">
                      v{file?.version}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownload(file?.id)}
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Eye"
                onClick={() => window.open(file?.url, '_blank')}
              />
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => onDelete(file?.id)}
                  className="text-error hover:text-error"
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {attachments?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Paperclip" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No attachments yet</p>
        </div>
      )}
    </div>
  );
};

export default FileAttachments;